/**
 * Como número, data e prazo aparecem na tela.
 *
 * Num arquivo só porque a inconsistência aqui é invisível até alguém comparar duas telas —
 * e aí o cliente pergunta por que a mesma confiança aparece como `96,3%` num lugar e
 * `96.33` no outro.
 */
import { agoraDoInquilino } from "./relogio";

/**
 * O fuso das horas que a tela escreve: o de Brasília, o do cockpit inteiro (o prazo, o
 * fechamento, o corte do dia). **Nunca o do aparelho**: num computador em Mato Grosso do
 * Sul (UTC-4) a passagem aparecia uma hora antes do prazo mostrado noutra tela, e o mesmo
 * dado lido em dois lugares tinha duas horas (`N252`).
 */
const BRASILIA = "America/Sao_Paulo";

/** Só a hora. Numa fila de trânsitos do dia, a data é ruído em toda linha. */
export function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { timeZone: BRASILIA });
}

/** Dia/mês e hora:minuto — para coluna de tabela, onde o ano e os segundos só quebram linha. */
export function dataCurta(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: BRASILIA,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function dataEHora(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { timeZone: BRASILIA });
}

/**
 * O que falta para um prazo vencer, em texto curto.
 *
 * `vencido` é uma palavra e não um número negativo de propósito: `-3h` faria o operador
 * fazer a conta de cabeça para concluir a mesma coisa, e a conclusão é a única coisa que
 * importa — aquele trânsito já perdeu a janela.
 */
export function restante(
  iso: string,
  agora: Date = agoraDoInquilino(),
): string {
  const ms = new Date(iso).getTime() - agora.getTime();
  if (ms <= 0) return "vencido";
  const minutos = Math.floor(ms / 60_000);
  const horas = Math.floor(minutos / 60);
  if (horas >= 24) return `${Math.floor(horas / 24)}d ${horas % 24}h`;
  if (horas > 0) return `${horas}h ${minutos % 60}m`;
  return `${minutos}m`;
}

/**
 * O prazo como relógio, quando ele está perto: "12:07" abaixo de uma hora, "vencido" depois,
 * e o `restante` de sempre quando ainda sobra folga. A urgência vem junto, para a cor:
 * `critico` abaixo de 10 minutos, `atencao` abaixo de 1 hora.
 */
export function contagem(
  iso: string,
  agora: Date = agoraDoInquilino(),
): { texto: string; urgencia: "vencido" | "critico" | "atencao" | "folga" } {
  const ms = new Date(iso).getTime() - agora.getTime();
  if (ms <= 0) return { texto: "vencido", urgencia: "vencido" };
  if (ms >= 3_600_000)
    return { texto: restante(iso, agora), urgencia: "folga" };
  const s = Math.floor(ms / 1000);
  const texto = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  return { texto, urgencia: ms < 600_000 ? "critico" : "atencao" };
}

/** `null` vira travessão e não `R$ 0,00`: "não sabemos" e "é zero" não são a mesma coisa. */
export function reais(centavos: number | null | undefined): string {
  if (centavos == null) return "—";
  return (centavos / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/**
 * O mesmo valor, curto o bastante para ser o número grande de um cartão.
 *
 * `R$ 16.573.986,52` tem dezessete caracteres e não cabe em 200px — e, pior, ninguém lê
 * dezessete caracteres de três metros de distância, que é a régua do §3 do design system.
 * `R$ 16,6 mi` responde a pergunta que o cartão faz. O valor exato não some: vai na linha de
 * contexto logo abaixo e no `title`, onde quem precisa conferir o encontra.
 *
 * Abaixo de mil, nada de abreviar: `R$ 847,20` já é curto e arredondar ali esconderia
 * centavo em cima de número pequeno, que é onde centavo aparece.
 */
export function reaisCompacto(centavos: number | null | undefined): string {
  if (centavos == null) return "—";
  const valor = centavos / 100;
  const absoluto = Math.abs(valor);
  const escala: [number, string][] = [
    [1_000_000_000, "bi"],
    [1_000_000, "mi"],
    [1_000, "mil"],
  ];
  for (const [divisor, sufixo] of escala) {
    if (absoluto >= divisor) {
      const curto = valor / divisor;
      // Uma casa decimal até 100, nenhuma acima: "R$ 16,6 mi" informa, "R$ 847,3 mil"
      // informa, e "R$ 1.234,5 mil" é só um número longo com sufixo.
      const casas = Math.abs(curto) < 100 ? 1 : 0;
      return `R$ ${curto.toLocaleString("pt-BR", {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      })} ${sufixo}`;
    }
  }
  return reais(centavos);
}

/**
 * Um inteiro com separador de milhar. `1350054` e `1.350.054` são o mesmo número e não são
 * a mesma leitura: sem os pontos, ninguém distingue um milhão de cem mil de relance.
 */
export function numeroLongo(valor: number | null | undefined): string {
  return valor == null ? "—" : valor.toLocaleString("pt-BR");
}

/**
 * Percentual com **vírgula**, não com ponto.
 *
 * `toFixed` é sempre inglês: devolve `98.6` em qualquer máquina, e num cockpit onde tudo
 * mais está em português essa é a linha que denuncia a tela como protótipo. Pior, `98.6`
 * ao lado de `1.359.011` põe o mesmo ponto significando duas coisas diferentes na mesma
 * tela — decimal num lugar, milhar no outro.
 */
export function percentual(valor: number | null | undefined): string {
  if (valor == null) return "—";
  return `${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/**
 * O tamanho de um arquivo como quem olha uma pasta o lê.
 *
 * Nunca devolve `0 KB`: um arquivo de 300 bytes existe, e mostrá-lo como zero faria o
 * operador achar que o upload falhou. Abaixo de 1 KiB o piso é `1 KB` — impreciso de
 * propósito, porque a precisão ali não decide nada e o zero decide errado.
 */
export function tamanhoLegivel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * A cor de uma confiança de leitura.
 *
 * Os cortes vêm da prévia e são decisão de produto, não estética: abaixo de 70 a leitura
 * não sustenta uma cobrança sozinha, e é isso que o vermelho está dizendo.
 */
export function corDaConfianca(
  valor: number | null | undefined,
): "verde" | "amarelo" | "vermelho" {
  if (valor == null) return "vermelho";
  if (valor >= 90) return "verde";
  if (valor >= 70) return "amarelo";
  return "vermelho";
}

/** `PROCESSAMENTO_7D` → `processamento 7d`. Sem tradução: o enum é o vocabulário da spec. */
export function rotuloDoPrazo(tipo: string): string {
  return tipo.replace(/_/g, " ").toLowerCase();
}

export function camera(nome: string): string {
  return nome.charAt(0) + nome.slice(1).toLowerCase();
}

/**
 * A matriz de divergência traduzida, com a cor que ela carrega.
 *
 * `indisponivel` é cinza e não vermelho: câmera que não leu não é divergência, é ausência
 * de evidência. Confundir as duas faria o operador investigar um conflito que não existe.
 */
export const PARIDADE: Record<
  string,
  { texto: string; cor: "verde" | "amarelo" | "vermelho" | "cinza" }
> = {
  igual: { texto: "igual", cor: "verde" },
  parcial: { texto: "divergência pontual", cor: "amarelo" },
  divergente: { texto: "divergente", cor: "vermelho" },
  indisponivel: { texto: "indisponível", cor: "cinza" },
};

export function paridade(valor: string | undefined) {
  return (
    PARIDADE[valor ?? "indisponivel"] ?? {
      texto: valor ?? "—",
      cor: "cinza" as const,
    }
  );
}

/** `720` → `30 dias`; `24` → `24 h`. */
export function duracaoDoRelogio(horas: number): string {
  if (horas < 48) return `${horas} h`;
  const dias = horas / 24;
  return `${Number.isInteger(dias) ? dias : dias.toFixed(1)} dias`;
}

/** O último dia de um período que acaba no instante `fim` — o dia como a norma o escreve. */
export function ultimoDia(fim: string): string {
  return new Date(Date.parse(fim) - 1).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

function diaDeBrasilia(instante: Date): string {
  return instante.toLocaleDateString("pt-BR", { timeZone: BRASILIA });
}

/** Só o dia, em Brasília: o começo e o fim de uma vigência. */
export function diaEmBrasilia(iso: string): string {
  return diaDeBrasilia(new Date(iso));
}

/** "14:05" no dia de `referencia`; "18/10 14:05" noutro dia — em Brasília. */
export function horaEmBrasilia(
  iso: string,
  referencia: Date = agoraDoInquilino(),
): string {
  const instante = new Date(iso);
  const hora = instante.toLocaleTimeString("pt-BR", {
    timeZone: BRASILIA,
    hour: "2-digit",
    minute: "2-digit",
  });
  if (diaDeBrasilia(instante) === diaDeBrasilia(referencia)) return hora;
  const dia = instante.toLocaleDateString("pt-BR", {
    timeZone: BRASILIA,
    day: "2-digit",
    month: "2-digit",
  });
  return `${dia} ${hora}`;
}
